import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import { labService } from '../../services/LabService';
import { adminConfigService } from '../../services/AdminConfigService';
import { imageLibraryService } from '../../services/imageLibraryService';
import {
  FlaskConical,
  Play,
  Loader2,
  RefreshCw,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle2,
  XCircle,
  Palette,
  Type,
  Layers,
  Sparkles,
  Copy,
  ExternalLink,
  History,
  Settings2,
  Shuffle,
  Filter,
  Video,
  Upload,
  ImagePlus,
  Package,
  Bot
} from 'lucide-react';
import ImagePromptEditor from '../../components/admin/ImagePromptEditor';

const LabPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Form state
  const [prompt, setPrompt] = useState('');
  const [sampleCount, setSampleCount] = useState(3);
  const [brandColors, setBrandColors] = useState(['#FFB800', '#000000']);
  const [primaryFont, setPrimaryFont] = useState('montserrat-bold');
  const [secondaryFont, setSecondaryFont] = useState('inter-regular');
  const [extraInstructions, setExtraInstructions] = useState('');
  // Layout agent selection
  const [availableAgents, setAvailableAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [shuffleLayouts, setShuffleLayouts] = useState(true);
  // Provider selection
  const [provider, setProvider] = useState('creatomate');
  const [templatedTemplates, setTemplatedTemplates] = useState([]);
  const [selectedTemplatedTemplates, setSelectedTemplatedTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  // React Render
  const [dimensions, setDimensions] = useState('square'); // 'square' | 'story'
  const [availableReactAgents, setAvailableReactAgents] = useState([]);
  // HyperFrames (video)
  const [isProduct, setIsProduct] = useState(false);
  const [description, setDescription] = useState('');
  const [durationSeconds, setDurationSeconds] = useState(12);
  const [libraryImages, setLibraryImages] = useState([]);
  const [selectedLibraryImages, setSelectedLibraryImages] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  // Data state
  const [session, setSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [feedbackInputs, setFeedbackInputs] = useState({});
  const [expandedSamples, setExpandedSamples] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [showConfig, setShowConfig] = useState(true);
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [useDraftPrompt, setUseDraftPrompt] = useState(true);
  const [error, setError] = useState(null);

  // Load sessions and agents on mount
  useEffect(() => {
    if (!currentUser?.is_admin) {
      navigate('/contents');
      return;
    }
    loadSessions();
    loadAvailableAgents();
  }, [currentUser, navigate]);

  const loadAvailableAgents = async () => {
    try {
      const response = await adminConfigService.getLabLayoutAgents();
      const agents = response.layout_agents || {};
      const entries = Object.entries(agents)
        .filter(([_, a]) => a.enabled !== false)
        .sort((a, b) => (a[1].order || 999) - (b[1].order || 999))
        .map(([id, a]) => ({ id, name: a.name || id }));
      setAvailableAgents(entries);
    } catch (err) {
      console.error('Error loading layout agents:', err);
    }
  };

  const loadAvailableReactAgents = async () => {
    try {
      const response = await adminConfigService.getLabReactLayoutAgents();
      const agents = response.layout_agents || {};
      const entries = Object.entries(agents)
        .filter(([_, a]) => a.enabled !== false)
        .sort((a, b) => (a[1].order || 999) - (b[1].order || 999))
        .map(([id, a]) => ({
          id,
          name: a.name || id,
          supported_dimensions: a.supported_dimensions || ['square'],
          needs_background_image: !!a.needs_background_image,
        }));
      setAvailableReactAgents(entries);
    } catch (err) {
      console.error('Error loading React layout agents:', err);
    }
  };

  const loadTemplatedTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await labService.listTemplatedTemplates({ limit: 50 });
      setTemplatedTemplates(response.data || []);
    } catch (err) {
      console.error('Error loading Templated.io templates:', err);
      setTemplatedTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadLibraryImages = async () => {
    setLoadingLibrary(true);
    try {
      const response = await imageLibraryService.listImages();
      setLibraryImages(response.data || []);
    } catch (err) {
      console.error('Error loading image library:', err);
      setLibraryImages([]);
    } finally {
      setLoadingLibrary(false);
    }
  };

  // Carregar templates do Templated.io quando provider mudar
  useEffect(() => {
    if (provider === 'templated' && templatedTemplates.length === 0) {
      loadTemplatedTemplates();
    }
    if (provider === 'react_render' && availableReactAgents.length === 0) {
      loadAvailableReactAgents();
    }
    if (provider === 'hyperframes') {
      setDimensions('story');
      if (sampleCount > 3) setSampleCount(2); // video e caro: default baixo
      if (libraryImages.length === 0) loadLibraryImages();
    }
    // Reset selected agents when switching provider
    setSelectedAgents([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  const toggleTemplatedTemplate = (templateId) => {
    setSelectedTemplatedTemplates(prev =>
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const toggleLibraryImage = (img) => {
    setSelectedLibraryImages(prev =>
      prev.some(i => i.id === img.id)
        ? prev.filter(i => i.id !== img.id)
        : [...prev, { id: img.id, image_url: img.image_url, caption: img.caption || '' }]
    );
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!imageLibraryService.helpers.isValidImage(file) || !imageLibraryService.helpers.isValidSize(file)) {
      setError('Imagem invalida (use JPG/PNG/WEBP/GIF ate 5MB)');
      e.target.value = '';
      return;
    }
    setUploadingImage(true);
    try {
      const res = await imageLibraryService.uploadImage(file);
      const item = res.data;
      if (item) {
        setLibraryImages(prev => [item, ...prev]);
        setSelectedLibraryImages(prev => [...prev, { id: item.id, image_url: item.image_url, caption: item.caption || '' }]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao subir imagem');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const loadSessions = async () => {
    try {
      const response = await labService.listSessions(10);
      setSessions(response.data || []);
    } catch (err) {
      console.error('Error loading sessions:', err);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setIsGenerating(true);
    setError(null);
    setSession(null);

    try {
      const params = {
        prompt: prompt.trim(),
        sample_count: sampleCount,
        mode: 'canvas',
        brand_colors: brandColors.filter(c => c),
        brand_fonts: {
          primary: primaryFont,
          secondary: secondaryFont
        },
        extra_instructions: extraInstructions.trim() || undefined,
        layout_agents:
          (provider === 'creatomate' || provider === 'react_render') && selectedAgents.length > 0
            ? selectedAgents
            : undefined,
        shuffle_layouts: shuffleLayouts,
        provider,
        templated_template_ids: provider === 'templated' && selectedTemplatedTemplates.length > 0
          ? selectedTemplatedTemplates
          : undefined,
        dimensions: (provider === 'react_render' || provider === 'gpt_image' || provider === 'hyperframes') ? dimensions : 'square',
        // modo video (hyperframes)
        description: provider === 'hyperframes' ? (description.trim() || undefined) : undefined,
        is_product: provider === 'hyperframes' ? isProduct : undefined,
        images: provider === 'hyperframes' && selectedLibraryImages.length > 0 ? selectedLibraryImages : undefined,
        duration_seconds: provider === 'hyperframes' ? durationSeconds : undefined,
        // Só o gpt_image usa o prompt configurável; nos outros providers o campo é ignorado.
        use_draft_prompt: provider === 'gpt_image' ? useDraftPrompt : false,
      };

      const response = await labService.generateSamples(params);
      setSession(response.data);
      setShowConfig(false);
      loadSessions();
    } catch (err) {
      const detail = err.response?.data?.detail;
      let errorMsg;
      if (Array.isArray(detail)) {
        errorMsg = detail.map(d => typeof d === 'object' ? d.msg : d).join('; ');
      } else if (typeof detail === 'object' && detail !== null) {
        errorMsg = detail.msg || JSON.stringify(detail);
      } else {
        errorMsg = detail || err.message || 'Erro ao gerar amostras';
      }
      setError(errorMsg);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = async (sampleId) => {
    if (!session) return;

    const feedback = feedbackInputs[sampleId] || '';
    setRegeneratingId(sampleId);

    try {
      const response = await labService.regenerateSample(session.id, sampleId, feedback);
      // Update sample in session
      setSession(prev => ({
        ...prev,
        samples: prev.samples.map(s =>
          s.id === sampleId ? response.data : s
        )
      }));
      setFeedbackInputs(prev => ({ ...prev, [sampleId]: '' }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao regenerar');
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleLoadSession = async (sessionId) => {
    try {
      const response = await labService.getSession(sessionId);
      setSession(response.data);
      setPrompt(response.data.prompt);
      setShowHistory(false);
      setShowConfig(false);
    } catch (err) {
      setError('Erro ao carregar sessao');
    }
  };

  const toggleSampleExpand = (sampleId) => {
    setExpandedSamples(prev => ({
      ...prev,
      [sampleId]: !prev[sampleId]
    }));
  };

  const toggleAgent = (agentId) => {
    setSelectedAgents(prev =>
      prev.includes(agentId)
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  const addColor = () => {
    if (brandColors.length < 6) {
      setBrandColors([...brandColors, '#333333']);
    }
  };

  const removeColor = (index) => {
    setBrandColors(brandColors.filter((_, i) => i !== index));
  };

  const updateColor = (index, value) => {
    const newColors = [...brandColors];
    newColors[index] = value;
    setBrandColors(newColors);
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <FlaskConical className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Laboratorio de Posts</h1>
                <p className="text-sm text-gray-500">Gere amostras, valide e itere no agente</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/admin/lab/review')}
                className="px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 bg-green-500 text-white hover:bg-green-600 transition-colors"
              >
                <CheckCircle2 className="w-4 h-4" />
                Validar Posts
              </button>
              <button
                onClick={() => setShowHistory(!showHistory)}
                className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                  showHistory ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <History className="w-4 h-4" />
                Historico
              </button>
              <button
                onClick={() => setShowConfig(!showConfig)}
                className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                  showConfig ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Settings2 className="w-4 h-4" />
                Config
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
            <XCircle className="w-4 h-4 flex-shrink-0" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* History Panel */}
        {showHistory && (
          <div className="mb-6 bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <History className="w-4 h-4" />
              Sessoes Recentes
            </h3>
            {sessions.length === 0 ? (
              <p className="text-gray-500 text-sm">Nenhuma sessao ainda</p>
            ) : (
              <div className="space-y-2">
                {sessions.map(s => (
                  <button
                    key={s.id}
                    onClick={() => handleLoadSession(s.id)}
                    className="w-full text-left p-3 rounded-lg border border-gray-100 hover:border-amber-300 hover:bg-amber-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 truncate max-w-md">
                        {s.prompt}
                      </span>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3 text-green-500" />
                          {s.completed}/{s.total_samples}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {(s.total_time_ms / 1000).toFixed(1)}s
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Config Panel */}
        {showConfig && (
          <div className="mb-6 bg-white rounded-xl border border-gray-200 p-5">
            {/* Prompt */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Briefing / Intencao
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Ex: Posts de marketing para venda de celulares Samsung Galaxy S24, destacando camera e performance, estilo moderno e premium..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {/* Sample Count */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Amostras
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  {[5, 10, 20, 35].map(n => (
                    <button
                      key={n}
                      onClick={() => setSampleCount(n)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        sampleCount === n
                          ? 'bg-amber-500 text-black'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                  <input
                    type="number"
                    value={sampleCount}
                    onChange={(e) => setSampleCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                    className="w-20 px-2 py-2 border border-gray-300 rounded-lg text-sm text-center"
                    min={1}
                    max={50}
                  />
                </div>
              </div>

              {/* Brand Colors */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Cores da Marca
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  {brandColors.map((color, i) => (
                    <div key={i} className="flex items-center gap-1">
                      <input
                        type="color"
                        value={color}
                        onChange={(e) => updateColor(i, e.target.value)}
                        className="w-8 h-8 rounded-lg cursor-pointer border border-gray-300"
                      />
                      <input
                        type="text"
                        value={color}
                        onChange={(e) => updateColor(i, e.target.value)}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-xs font-mono"
                      />
                      {brandColors.length > 1 && (
                        <button onClick={() => removeColor(i)} className="text-gray-400 hover:text-red-500">
                          <XCircle className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                  {brandColors.length < 6 && (
                    <button onClick={addColor} className="text-amber-600 text-xs hover:underline">+cor</button>
                  )}
                </div>
              </div>

              {/* Fonts */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Type className="w-4 h-4" />
                  Fontes
                </label>
                <div className="space-y-2">
                  <select
                    value={primaryFont}
                    onChange={(e) => setPrimaryFont(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="montserrat-bold">Montserrat Bold</option>
                    <option value="poppins-bold">Poppins Bold</option>
                    <option value="Inter-Bold">Inter Bold</option>
                    <option value="roboto-bold">Roboto Bold</option>
                    <option value="oswald-bold">Oswald Bold</option>
                    <option value="raleway-bold">Raleway Bold</option>
                    <option value="nunito-bold">Nunito Bold</option>
                    <option value="lato-bold">Lato Bold</option>
                    <option value="playfairdisplay-bold">Playfair Display Bold</option>
                    <option value="bangers-regular">Bangers</option>
                    <option value="lobster-regular">Lobster</option>
                    <option value="spacemono-bold">Space Mono Bold</option>
                  </select>
                  <select
                    value={secondaryFont}
                    onChange={(e) => setSecondaryFont(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="inter-regular">Inter Regular</option>
                    <option value="poppins-regular">Poppins Regular</option>
                    <option value="roboto-regular">Roboto Regular</option>
                    <option value="opensans-regular">Open Sans Regular</option>
                    <option value="lato-regular">Lato Regular</option>
                    <option value="montserrat-regular">Montserrat Regular</option>
                    <option value="nunito-regular">Nunito Regular</option>
                    <option value="raleway-regular">Raleway Regular</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Extra Instructions */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Instrucoes Extras para o Agente (opcional)
              </label>
              <textarea
                value={extraInstructions}
                onChange={(e) => setExtraInstructions(e.target.value)}
                placeholder="Ex: Use cores escuras para fundo, texto branco. Priorize imagens de smartphones. Estilo minimalista..."
                rows={2}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
              />
            </div>

            {/* Provider Selection */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Provider de Renderizacao
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setProvider('creatomate')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    provider === 'creatomate'
                      ? 'bg-amber-500 text-black'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Creatomate (Canvas)
                </button>
                <button
                  onClick={() => setProvider('templated')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    provider === 'templated'
                      ? 'bg-purple-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Templated.io (Templates)
                </button>
                <button
                  onClick={() => setProvider('react_render')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    provider === 'react_render'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  React Render (GPT-4.1)
                </button>
                <button
                  onClick={() => setProvider('gpt_image')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    provider === 'gpt_image'
                      ? 'bg-emerald-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  GPT Image (gpt-image-2)
                </button>
                <button
                  onClick={() => setProvider('hyperframes')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    provider === 'hyperframes'
                      ? 'bg-rose-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Video className="w-4 h-4" />
                  Video (HyperFrames)
                </button>
              </div>
            </div>

            {/* Dimensions Selection (react_render, gpt_image ou hyperframes) */}
            {(provider === 'react_render' || provider === 'gpt_image' || provider === 'hyperframes') && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Dimensoes do Post
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setDimensions('square')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      dimensions === 'square'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Feed 1080x1080
                  </button>
                  <button
                    onClick={() => setDimensions('story')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      dimensions === 'story'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Story 1080x1920
                  </button>
                </div>
              </div>
            )}

            {/* Prompt do agente de imagem — rascunho editável sem afetar producao */}
            {provider === 'gpt_image' && (
              <div className="mb-4 border border-blue-100 bg-blue-50/40 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowPromptEditor(!showPromptEditor)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-blue-50 transition-colors"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <Bot className="w-4 h-4 text-blue-600" />
                    Prompt do agente
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[11px] font-medium">
                      {useDraftPrompt ? 'rascunho' : 'produção'}
                    </span>
                  </span>
                  {showPromptEditor
                    ? <ChevronUp className="w-4 h-4 text-gray-500" />
                    : <ChevronDown className="w-4 h-4 text-gray-500" />}
                </button>

                {showPromptEditor && (
                  <div className="px-4 pb-4 space-y-4">
                    {/* Escolhe qual prompt esta geracao vai usar — permite comparar A/B */}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setUseDraftPrompt(true)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          useDraftPrompt
                            ? 'bg-blue-500 text-white'
                            : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        Gerar com o rascunho
                      </button>
                      <button
                        type="button"
                        onClick={() => setUseDraftPrompt(false)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          !useDraftPrompt
                            ? 'bg-gray-900 text-white'
                            : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        Gerar com o de produção
                      </button>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-gray-200">
                      <ImagePromptEditor mode="draft" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* HyperFrames (video) — descricao, produto, imagens, duracao */}
            {provider === 'hyperframes' && (
              <div className="mb-4 space-y-4 p-4 bg-rose-50/50 border border-rose-100 rounded-xl">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Descricao (vai direto para o agente de video)
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Ex: Divulgar o fone Pulse X. A imagem 1 e o produto (use como heroi flutuante). Destaque cancelamento de ruido e 36h de bateria. Fecha com CTA 'Saiba mais'."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500 resize-none"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <button
                    onClick={() => setIsProduct(!isProduct)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isProduct ? 'bg-rose-500 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Package className="w-4 h-4" />
                    {isProduct ? 'E um produto' : 'Marcar como produto'}
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Duracao</span>
                    {[8, 12, 15, 20].map(d => (
                      <button
                        key={d}
                        onClick={() => setDurationSeconds(d)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          durationSeconds === d ? 'bg-rose-500 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {d}s
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <ImagePlus className="w-4 h-4" />
                      Imagens (logos / produto)
                      <span className="font-normal text-gray-400">
                        {selectedLibraryImages.length > 0 ? `(${selectedLibraryImages.length} selecionadas)` : '(opcional — teste com e sem)'}
                      </span>
                    </label>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 cursor-pointer transition-colors">
                        {uploadingImage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        Upload
                        <input type="file" accept="image/*" onChange={handleUploadImage} className="hidden" disabled={uploadingImage} />
                      </label>
                      <button
                        onClick={loadLibraryImages}
                        disabled={loadingLibrary}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${loadingLibrary ? 'animate-spin' : ''}`} />
                        Atualizar
                      </button>
                    </div>
                  </div>
                  {loadingLibrary ? (
                    <div className="flex items-center justify-center py-6 text-gray-400">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando biblioteca...
                    </div>
                  ) : libraryImages.length === 0 ? (
                    <div className="text-center py-6 text-gray-400 text-sm bg-white rounded-lg border border-dashed border-gray-200">
                      Biblioteca vazia. Faca upload de um logo/produto acima.
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                      {libraryImages.map(img => {
                        const selected = selectedLibraryImages.some(i => i.id === img.id);
                        const order = selectedLibraryImages.findIndex(i => i.id === img.id) + 1;
                        return (
                          <button
                            key={img.id}
                            onClick={() => toggleLibraryImage(img)}
                            className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                              selected ? 'border-rose-500 ring-2 ring-rose-200' : 'border-gray-200 hover:border-rose-300'
                            }`}
                            title={img.caption || ''}
                          >
                            <img src={img.image_url} alt={img.caption || 'imagem'} className="w-full aspect-square object-cover bg-gray-100" />
                            {selected && (
                              <span className="absolute top-1 right-1 w-5 h-5 bg-rose-500 text-white rounded-full text-xs font-bold flex items-center justify-center">
                                {order}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {selectedLibraryImages.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      As imagens vao numeradas pro agente (1, 2, ...). Diga no prompt/descricao como usar cada uma.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Templated.io Template Selection */}
            {provider === 'templated' && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    Templates Templated.io
                    <span className="font-normal text-gray-400">
                      {selectedTemplatedTemplates.length === 0
                        ? '(selecione pelo menos 1)'
                        : `(${selectedTemplatedTemplates.length} selecionados)`}
                    </span>
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShuffleLayouts(!shuffleLayouts)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        shuffleLayouts
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                      title={shuffleLayouts ? 'Aleatorio: ON' : 'Aleatorio: OFF'}
                    >
                      <Shuffle className="w-3.5 h-3.5" />
                      {shuffleLayouts ? 'Aleatorio' : 'Sequencial'}
                    </button>
                    <button
                      onClick={loadTemplatedTemplates}
                      disabled={loadingTemplates}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${loadingTemplates ? 'animate-spin' : ''}`} />
                      Atualizar
                    </button>
                  </div>
                </div>

                {loadingTemplates ? (
                  <div className="flex items-center justify-center py-8 text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Carregando templates...
                  </div>
                ) : templatedTemplates.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    Nenhum template encontrado. Verifique a chave TEMPLATED_API_KEY.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {templatedTemplates.map(t => (
                      <button
                        key={t.id}
                        onClick={() => toggleTemplatedTemplate(t.id)}
                        className={`relative p-2 rounded-xl border-2 transition-all text-left ${
                          selectedTemplatedTemplates.includes(t.id)
                            ? 'border-purple-500 bg-purple-50 shadow-sm'
                            : 'border-gray-200 hover:border-purple-300 bg-white'
                        }`}
                      >
                        {t.thumbnail ? (
                          <img
                            src={t.thumbnail}
                            alt={t.name}
                            className="w-full aspect-square object-cover rounded-lg mb-2 bg-gray-100"
                          />
                        ) : (
                          <div className="w-full aspect-square rounded-lg mb-2 bg-gray-100 flex items-center justify-center">
                            <Layers className="w-8 h-8 text-gray-300" />
                          </div>
                        )}
                        <p className="text-xs font-medium text-gray-900 truncate">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.layer_count} layers</p>
                        {selectedTemplatedTemplates.includes(t.id) && (
                          <div className="absolute top-1 right-1 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                            <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Layout Agents Selection (Creatomate only) */}
            {provider === 'creatomate' && availableAgents.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    Layout Agents
                    <span className="font-normal text-gray-400">
                      {selectedAgents.length === 0
                        ? `(todos ${availableAgents.length})`
                        : `(${selectedAgents.length} selecionados)`}
                    </span>
                  </label>
                  <button
                    onClick={() => setShuffleLayouts(!shuffleLayouts)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      shuffleLayouts
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                    title={shuffleLayouts ? 'Aleatorio: ON — layouts distribuidos aleatoriamente' : 'Aleatorio: OFF — GPT-4 decide a distribuicao'}
                  >
                    <Shuffle className="w-3.5 h-3.5" />
                    {shuffleLayouts ? 'Aleatorio' : 'Sequencial'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    onClick={() => setSelectedAgents([])}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                      selectedAgents.length === 0
                        ? 'bg-amber-500 text-black'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Todos
                  </button>
                  {availableAgents.map(agent => (
                    <button
                      key={agent.id}
                      onClick={() => toggleAgent(agent.id)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                        selectedAgents.includes(agent.id)
                          ? 'bg-amber-500 text-black'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {agent.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* React Layout Agents Selection (apenas react_render) */}
            {provider === 'react_render' && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    React Layouts
                    <span className="font-normal text-gray-400">
                      {(() => {
                        const compatible = availableReactAgents.filter(a => (a.supported_dimensions || ['square']).includes(dimensions));
                        return selectedAgents.length === 0
                          ? `(todos ${compatible.length} compativeis com ${dimensions})`
                          : `(${selectedAgents.length} selecionados)`;
                      })()}
                    </span>
                  </label>
                  <button
                    onClick={() => setShuffleLayouts(!shuffleLayouts)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      shuffleLayouts
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <Shuffle className="w-3.5 h-3.5" />
                    {shuffleLayouts ? 'Aleatorio' : 'Sequencial'}
                  </button>
                </div>
                {availableReactAgents.length === 0 ? (
                  <div className="text-sm text-gray-400 py-3 text-center bg-gray-50 rounded-lg">
                    Nenhum React agent configurado. Rode <code>python -m scripts.seed_react_lab_agents</code> no backend.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => setSelectedAgents([])}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                        selectedAgents.length === 0
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Todos
                    </button>
                    {availableReactAgents
                      .filter(a => (a.supported_dimensions || ['square']).includes(dimensions))
                      .map(agent => (
                        <button
                          key={agent.id}
                          onClick={() => toggleAgent(agent.id)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                            selectedAgents.includes(agent.id)
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          title={agent.needs_background_image ? 'Gera background com Flux' : 'So tipografia/icones'}
                        >
                          {agent.name}
                          {agent.needs_background_image && <span className="opacity-70">+BG</span>}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={
                isGenerating ||
                !prompt.trim() ||
                (provider === 'templated' && selectedTemplatedTemplates.length === 0) ||
                (provider === 'react_render' && availableReactAgents.length === 0)
              }
              className={`w-full py-3 ${
                provider === 'templated'
                  ? 'bg-purple-500 hover:bg-purple-600 text-white'
                  : provider === 'react_render'
                    ? 'bg-blue-500 hover:bg-blue-600 text-white'
                    : provider === 'gpt_image'
                      ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                      : provider === 'hyperframes'
                        ? 'bg-rose-500 hover:bg-rose-600 text-white'
                        : 'bg-amber-500 hover:bg-amber-600 text-black'
              } disabled:bg-gray-300 disabled:text-gray-500 font-semibold rounded-xl transition-colors flex items-center justify-center gap-2`}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Gerando {sampleCount} amostras via {
                    provider === 'templated' ? 'Templated.io' :
                    provider === 'react_render' ? `React Render (${dimensions})` :
                    provider === 'gpt_image' ? `GPT Image (${dimensions})` :
                    provider === 'hyperframes' ? `HyperFrames video (${dimensions})` :
                    'Creatomate'
                  }...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  Gerar {sampleCount} Amostras
                  {provider === 'templated' && ' (Templated.io)'}
                  {provider === 'react_render' && ` (React ${dimensions})`}
                  {provider === 'gpt_image' && ` (GPT Image ${dimensions})`}
                  {provider === 'hyperframes' && ` (Video ${dimensions})`}
                </>
              )}
            </button>
          </div>
        )}

        {/* Results */}
        {session && (
          <div>
            {/* Session Header */}
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  Resultados
                  <span className="text-sm font-normal text-gray-500">
                    ({session.summary?.completed || 0}/{session.summary?.total || 0} gerados)
                  </span>
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Tempo total: {(session.total_time_ms / 1000).toFixed(1)}s
                </p>
              </div>
              <button
                onClick={() => { setShowConfig(true); }}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Nova Geracao
              </button>
            </div>

            {/* Samples Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {session.samples?.map((sample) => (
                <SampleCard
                  key={sample.id}
                  sample={sample}
                  isExpanded={expandedSamples[sample.id]}
                  onToggleExpand={() => toggleSampleExpand(sample.id)}
                  feedback={feedbackInputs[sample.id] || ''}
                  onFeedbackChange={(val) => setFeedbackInputs(prev => ({ ...prev, [sample.id]: val }))}
                  onRegenerate={() => handleRegenerate(sample.id)}
                  isRegenerating={regeneratingId === sample.id}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!session && !isGenerating && !showConfig && (
          <div className="text-center py-20">
            <FlaskConical className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700">Nenhuma sessao ativa</h3>
            <p className="text-gray-500 text-sm mt-1">
              Abra a configuracao para gerar amostras
            </p>
            <button
              onClick={() => setShowConfig(true)}
              className="mt-4 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium text-sm"
            >
              Abrir Config
            </button>
          </div>
        )}
      </div>
    </div>
  );
};


// ============================================
// SAMPLE CARD COMPONENT
// ============================================

const SampleCard = ({
  sample,
  isExpanded,
  onToggleExpand,
  feedback,
  onFeedbackChange,
  onRegenerate,
  isRegenerating
}) => {
  const isCompleted = sample.status === 'completed';
  const [showLayers, setShowLayers] = useState(false);

  return (
    <div className={`bg-white rounded-xl border overflow-hidden transition-shadow ${
      isCompleted ? 'border-gray-200 hover:shadow-md' : 'border-red-200'
    }`}>
      {/* Media */}
      {isCompleted && sample.content_type === 'video' && sample.video_url ? (
        <div className="relative bg-black">
          <video
            src={sample.video_url}
            controls
            loop
            playsInline
            className="w-full aspect-[9/16] max-h-[520px] object-contain bg-black"
          />
          <a
            href={sample.video_url}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-full shadow-lg"
          >
            <ExternalLink className="w-4 h-4 text-gray-700" />
          </a>
        </div>
      ) : isCompleted && sample.image_url ? (
        <div className="relative group">
          <img
            src={sample.image_url}
            alt={sample.template_title}
            className="w-full aspect-square object-cover"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
            <a
              href={sample.image_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 bg-white rounded-full shadow-lg"
            >
              <ExternalLink className="w-5 h-5 text-gray-700" />
            </a>
          </div>
        </div>
      ) : (
        <div className="w-full aspect-square bg-gray-100 flex items-center justify-center">
          {sample.status === 'failed' ? (
            <div className="text-center p-4">
              <XCircle className="w-10 h-10 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-red-600">{sample.error_message || 'Falha na geracao'}</p>
            </div>
          ) : (
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          )}
        </div>
      )}

      {/* Info */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">{sample.template_title}</h3>
            {sample.layout_agent_name && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 shrink-0">
                {sample.layout_agent_name}
              </span>
            )}
            {sample.provider === 'templated' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 shrink-0">
                Templated
              </span>
            )}
            {sample.provider === 'react_render' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 shrink-0">
                React {sample.layers?.dimensions || ''}
              </span>
            )}
            {sample.provider === 'gpt_image' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 shrink-0">
                gpt-image-2 {sample.layers?.dimensions || ''}
              </span>
            )}
            {sample.provider === 'hyperframes' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800 shrink-0">
                Video {sample.layers?.dimensions || ''}{sample.layers?.duration ? ` ${Math.round(sample.layers.duration)}s` : ''}
              </span>
            )}
          </div>
          <span className="text-xs text-gray-500 flex items-center gap-1 shrink-0">
            <Clock className="w-3 h-3" />
            {(sample.generation_time_ms / 1000).toFixed(1)}s
          </span>
        </div>

        <p className="text-xs text-gray-500 mb-3">
          Layout: <span className="font-mono font-semibold">{sample.layout_type || sample.template_id}</span>
        </p>

        {/* JSX viewer (react_render) */}
        {sample.provider === 'react_render' && sample.layers?.jsx_code && (
          <div className="mb-3">
            <button
              onClick={() => setShowLayers(!showLayers)}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              <Layers className="w-3 h-3" />
              {showLayers ? 'Esconder' : 'Ver'} JSX gerado ({sample.layers.jsx_code.length} chars)
              {showLayers ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showLayers && (
              <div className="mt-2 p-3 bg-gray-900 rounded-lg max-h-64 overflow-auto">
                {sample.layers.design_notes && (
                  <p className="text-xs text-blue-300 mb-2 italic">{sample.layers.design_notes}</p>
                )}
                <pre className="text-xs font-mono text-gray-100 whitespace-pre-wrap">
                  {sample.layers.jsx_code}
                </pre>
                <button
                  onClick={() => navigator.clipboard.writeText(sample.layers.jsx_code)}
                  className="mt-2 flex items-center gap-1 text-xs text-blue-300 hover:text-blue-200"
                >
                  <Copy className="w-3 h-3" />
                  Copiar JSX
                </button>
              </div>
            )}
          </div>
        )}

        {/* HTML viewer (hyperframes) */}
        {sample.provider === 'hyperframes' && sample.layers?.html && (
          <div className="mb-3">
            <button
              onClick={() => setShowLayers(!showLayers)}
              className="flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800 font-medium"
            >
              <Layers className="w-3 h-3" />
              {showLayers ? 'Esconder' : 'Ver'} HTML da composicao ({sample.layers.html.length} chars)
              {showLayers ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showLayers && (
              <div className="mt-2 p-3 bg-gray-900 rounded-lg max-h-64 overflow-auto">
                {sample.layers.design_notes && (
                  <p className="text-xs text-rose-300 mb-2 italic">{sample.layers.design_notes}</p>
                )}
                <pre className="text-xs font-mono text-gray-100 whitespace-pre-wrap">
                  {sample.layers.html}
                </pre>
                <button
                  onClick={() => navigator.clipboard.writeText(sample.layers.html)}
                  className="mt-2 flex items-center gap-1 text-xs text-rose-300 hover:text-rose-200"
                >
                  <Copy className="w-3 h-3" />
                  Copiar HTML
                </button>
              </div>
            )}
          </div>
        )}

        {/* Layer details toggle (creatomate / templated) */}
        {sample.provider !== 'react_render' && sample.provider !== 'hyperframes' && sample.layers && Object.keys(sample.layers).length > 0 && (
          <div className="mb-3">
            <button
              onClick={() => setShowLayers(!showLayers)}
              className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 font-medium"
            >
              <Layers className="w-3 h-3" />
              {showLayers ? 'Esconder' : 'Ver'} Layers ({Object.keys(sample.layers).length})
              {showLayers ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showLayers && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-1.5">
                {Object.entries(sample.layers).map(([name, data]) => (
                  <div key={name} className="text-xs">
                    <span className="font-mono font-bold text-gray-700">{name}:</span>{' '}
                    {data.text && <span className="text-gray-600">"{data.text}"</span>}
                    {data.image && (
                      <span className="text-blue-500 truncate block ml-4">
                        img: {data.image.substring(0, 50)}...
                      </span>
                    )}
                    {data.text_color && (
                      <span className="ml-2 inline-flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full inline-block border border-gray-300" style={{ backgroundColor: data.text_color }} />
                        <span className="font-mono text-gray-500">{data.text_color}</span>
                      </span>
                    )}
                    {data.font && <span className="ml-2 text-gray-500 font-mono">[{data.font}]</span>}
                    {data.background_color && (
                      <span className="ml-2 inline-flex items-center gap-1">
                        bg:
                        <span className="w-3 h-3 rounded-full inline-block border border-gray-300" style={{ backgroundColor: data.background_color }} />
                      </span>
                    )}
                    {data.hide && <span className="ml-2 text-red-400">(hidden)</span>}
                  </div>
                ))}
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(sample.layers, null, 2));
                  }}
                  className="mt-2 flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  <Copy className="w-3 h-3" />
                  Copiar JSON
                </button>
              </div>
            )}
          </div>
        )}

        {/* Agent messages toggle */}
        {sample.agent_messages?.length > 0 && (
          <div className="mb-3">
            <button
              onClick={onToggleExpand}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 font-medium"
            >
              <MessageSquare className="w-3 h-3" />
              {isExpanded ? 'Esconder' : 'Ver'} Log do Agente ({sample.agent_messages.length})
            </button>
            {isExpanded && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg max-h-40 overflow-y-auto space-y-1">
                {sample.agent_messages.map((msg, i) => (
                  <div key={i} className="text-xs">
                    <span className={`font-bold ${
                      msg.role === 'error' ? 'text-red-500' :
                      msg.role === 'tool' ? 'text-blue-500' :
                      'text-gray-600'
                    }`}>
                      [{msg.role}]
                    </span>{' '}
                    <span className="text-gray-600">{msg.content?.substring(0, 200)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Feedback + Regenerate */}
        <div className="border-t border-gray-100 pt-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={feedback}
              onChange={(e) => onFeedbackChange(e.target.value)}
              placeholder="Feedback para melhorar..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
            />
            <button
              onClick={onRegenerate}
              disabled={isRegenerating}
              className="px-3 py-2 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-400 text-white rounded-lg text-xs font-medium flex items-center gap-1 transition-colors"
            >
              {isRegenerating ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
              Regenerar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabPage;
